import { Flex, Table } from 'antd';
import { useEffect, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';

export interface ServiceData {
  serviceType?: string;
  screenedTime?: string;
  unScreenedTime?: string;
  time?: string;
}

interface DropoffTableData {
  key: string;
  service: string;
  usDomesticScreened: string;
  usDomesticUnscreened: string;
  internationalScreened: string;
  internationalUnscreened: string;
}

interface PickupTableData {
  key: string;
  service: string;
  usDomestic: string;
  international: string;
}

interface UATimesTableProps {
  data?: ServiceData[];
  selectedTab: 'dropoff' | 'pickup';
}

// Dropoff columns
const columnsDropoff: ColumnsType<DropoffTableData> = [
  {
    title: 'Service',
    dataIndex: 'service',
    key: 'service',
  },
  {
    title: 'US Domestic',
    children: [
      {
        title: 'Screened',
        dataIndex: 'usDomesticScreened',
        key: 'usDomesticScreened',
        width: 110,
      },
      {
        title: 'Unscreened',
        dataIndex: 'usDomesticUnscreened',
        key: 'usDomesticUnscreened',
        width: 110,
      },
    ],
  },
  {
    title: 'International',
    children: [
      {
        title: 'Screened',
        dataIndex: 'internationalScreened',
        key: 'internationalScreened',
        width: 110,
      },
      {
        title: 'Unscreened',
        dataIndex: 'internationalUnscreened',
        key: 'internationalUnscreened',
        width: 110,
      },
    ],
  },
];

// Pickup columns
const columnsPickup: ColumnsType<PickupTableData> = [
  {
    title: 'Service',
    dataIndex: 'service',
    key: 'service',
  },
  {
    title: 'US Domestic',
    dataIndex: 'usDomestic',
    key: 'usDomestic',
  },
  {
    title: 'International',
    dataIndex: 'international',
    key: 'international',
  },
];

/**
 * UATimesTable Component
 *
 * Displays United Airlines service times in a table format.
 * Supports both dropoff and pickup views with different column
 * structures for screened/unscreened and domestic/international services.
 */
const UATimesTable = ({ data = [], selectedTab }: UATimesTableProps) => {
  const [dataDropoff, setDataDropoff] = useState<DropoffTableData[]>([]);
  const [dataPickup, setDataPickup] = useState<PickupTableData[]>([]);

  // Adjusting for Dropoff times
  useEffect(() => {
    if (data?.length) {
      const formattedDropoffData = data.map((item, index) => ({
        key: String(index + 1), // Unique key for each row
        service: item.serviceType, // Service name (e.g., "Intra-U.S Bulk")
        usDomesticScreened: item.screenedTime || 'N/A', // US Domestic Screened
        usDomesticUnscreened: item.unScreenedTime || 'N/A', // US Domestic Unscreened
        internationalScreened: item.screenedTime || 'N/A', // International Screened
        internationalUnscreened: item.unScreenedTime || 'N/A', // International Unscreened
      }));
      setDataDropoff(formattedDropoffData);
    }
  }, [data]);

  // Adjusting for Pickup times
  useEffect(() => {
    if (data?.length) {
      const formattedPickupData = data.map((item, index) => ({
        key: String(index + 1), // Unique key for each row
        service: item.serviceType, // Service name (e.g., "Intra-U.S Bulk")
        usDomestic: item.time || 'N/A', // Pickup time for US Domestic
        international: item.time || 'N/A', // Pickup time for International
      }));
      setDataPickup(formattedPickupData);
    }
  }, [data]);

  return (
    <Flex vertical gap={12}>
      {selectedTab === 'dropoff' ? (
        <Table
          columns={columnsDropoff}
          dataSource={dataDropoff}
          pagination={false}
          rowHoverable={false}
          bordered
          className='zl-custom-times-table'
        />
      ) : (
        <Table
          columns={columnsPickup}
          dataSource={dataPickup}
          pagination={false}
          bordered
          className='zl-custom-times-table'
        />
      )}
    </Flex>
  );
};

export default UATimesTable;
